import { copyMarketplaceDashboard } from "@/api/hooks/profile";
import { useMarketplaceImage } from "@/marketplace/hooks";
import "@/marketplace/marketplace-dashboard-list/marketplace-dashboard-list-item.scss";
import { useAppDispatch } from "@/reducer";
import { t } from "@lingui/core/macro";
import { Card } from "@mm/metx-design-system";
import type { MarketplaceDashboardCard, SlimProfile } from "@mm/metx-workbench.meteomatics.com";
import { type MouseEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

const ADDED_FEEDBACK_MS = 10_000;

type MarketplaceDashboardListItemProps = {
  dashboard: MarketplaceDashboardCard;
  detailHref?: string;
  loading?: boolean;
  onAddSuccess: (dashboardTitle: string, copiedProfile: SlimProfile) => void;
  onAddError: (description?: string) => void;
};

export function MarketplaceDashboardListItem({
  dashboard,
  detailHref,
  loading = false,
  onAddSuccess,
  onAddError,
}: MarketplaceDashboardListItemProps) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isCopying, setIsCopying] = useState(false);
  const [showAdded, setShowAdded] = useState(false);
  const imageSrc = useMarketplaceImage(dashboard.image_url);

  useEffect(() => {
    if (!showAdded) {
      return;
    }

    const timeoutId = window.setTimeout(() => setShowAdded(false), ADDED_FEEDBACK_MS);
    return () => window.clearTimeout(timeoutId);
  }, [showAdded]);

  const handleAddDashboard = async (event: MouseEvent) => {
    event.stopPropagation();
    if (loading || showAdded || isCopying) {
      return;
    }

    setIsCopying(true);
    try {
      const copiedProfile = await dispatch(copyMarketplaceDashboard(dashboard.id)).unwrap();
      setShowAdded(true);
      onAddSuccess(dashboard.title, copiedProfile);
    } catch (error) {
      console.error("Failed to copy marketplace dashboard:", error);
      onAddError(error instanceof Error ? error.message : undefined);
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <Card
      className="marketplace-card"
      loading={loading}
      imageSrc={imageSrc}
      imageAlt={dashboard.title}
      href={detailHref}
      onClick={
        detailHref
          ? (event) => {
              event.preventDefault();
              history.push(detailHref);
            }
          : undefined
      }
      title={
        <span className="marketplace-card__title">
          {dashboard.title}
          <span className="material-symbols-outlined marketplace-card__title-icon" aria-hidden>
            arrow_outward
          </span>
        </span>
      }
      footerMeta={dashboard.tier ?? undefined}
      body={dashboard.description ?? ""}
      primaryAction={{
        label: showAdded
          ? t({ id: "marketplace.added", message: "Added" })
          : t({ id: "marketplace.add-dashboard", message: "Add dashboard" }),
        icon: showAdded ? (
          <span className="material-symbols-outlined">check</span>
        ) : isCopying ? (
          <span className="material-symbols-outlined marketplace-card__action-icon--spin">progress_activity</span>
        ) : (
          <span className="material-symbols-outlined">add</span>
        ),
        disabled: showAdded || isCopying,
        onClick: handleAddDashboard,
      }}
    />
  );
}
